import { useState } from "react";
import { Command } from "cmdk";
import { MessageCircleQuestion, Search } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { Modal } from "@web/ui/Modal";
import { Shortcut } from "@web/ui/Kbd";
import { AccountChip } from "@web/ui/AccountChip";
import { useAccountLookup } from "@web/lib/accounts";
import { hasCommand, runCommand, useCommandsVersion } from "./commands";
import { COMMANDS, GROUP_ORDER, type CommandId } from "./keymap";

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

/** Cmd/Ctrl+K: every available command, with its shortcut, so the palette also teaches keys. */
export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  useCommandsVersion();
  const navigate = useNavigate();
  const { list: accounts, colorIndex, label } = useAccountLookup();
  // What the owner typed: also a question they can ask about their mail (U11).
  const [typed, setTyped] = useState("");
  const question = typed.trim();

  const available = COMMANDS.filter((c) => c.id !== "palette.open" && hasCommand(c.id));

  const close = () => {
    setTyped("");
    onClose();
  };

  const run = (id: CommandId) => {
    close();
    // Let the dialog close (and focus return) before the command moves focus itself.
    requestAnimationFrame(() => runCommand(id));
  };

  return (
    <Modal open={open} onClose={close} label="Command palette">
      <Command label="Command palette" loop className="flex flex-col">
        <div className="flex items-center gap-2 border-b border-line px-4">
          <Search aria-hidden className="size-4 text-muted" />
          <Command.Input
            value={typed}
            onValueChange={setTyped}
            placeholder="Type a command or a question…"
            className="h-12 flex-1 bg-transparent text-[15px] text-ink outline-none placeholder:text-faint"
          />
        </div>
        <Command.List className="py-2">
          {question ? null : <Command.Empty>No matching command.</Command.Empty>}
          {GROUP_ORDER.map((group) => {
            const items = available.filter((c) => c.group === group);
            if (!items.length) return null;
            return (
              <Command.Group key={group} heading={group}>
                {items.map((c) => (
                  <Command.Item
                    key={c.id}
                    value={c.label}
                    keywords={c.keywords ? [...c.keywords] : undefined}
                    onSelect={() => run(c.id)}
                  >
                    <span className="flex-1">{c.label}</span>
                    {c.keys[0] ? <Shortcut binding={c.keys[0]} /> : null}
                  </Command.Item>
                ))}
              </Command.Group>
            );
          })}
          {question.length >= 3 ? (
            <Command.Group heading="Ask">
              <Command.Item
                forceMount
                value={`ask about your mail ${question}`}
                onSelect={() => {
                  close();
                  void navigate({ to: "/ask", search: { q: question } });
                }}
              >
                <MessageCircleQuestion aria-hidden className="size-4 text-muted" />
                <span className="flex-1 truncate">Ask about your mail: “{question}”</span>
              </Command.Item>
            </Command.Group>
          ) : null}
          {accounts.length > 1 ? (
            <Command.Group heading="Show one account">
              {accounts.map((a) => (
                <Command.Item
                  key={a.id}
                  value={`Show only ${a.email}`}
                  onSelect={() => {
                    close();
                    void navigate({ to: "/inbox", search: { account: a.id } });
                  }}
                >
                  <span className="flex-1">Show only {a.email}</span>
                  <AccountChip
                    email={a.email}
                    label={label(a.email)}
                    colorIndex={colorIndex(a.id)}
                  />
                </Command.Item>
              ))}
              <Command.Item
                value="Show all accounts"
                onSelect={() => {
                  close();
                  void navigate({ to: "/inbox", search: {} });
                }}
              >
                <span className="flex-1">Show all accounts</span>
              </Command.Item>
            </Command.Group>
          ) : null}
        </Command.List>
      </Command>
    </Modal>
  );
}
